import { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, FileText, Shield, Trash2, XCircle, Loader2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { API } from '../config/api';
import { useLegalVisible } from '../hooks/useLegalVisible';

const MOTIFS = [
  { v: '', l: 'Résiliation standard (fin de période)' },
  { v: 'demenagement_30km', l: 'Déménagement à plus de 30 km' },
  { v: 'blessure_maladie', l: 'Blessure ou maladie empêchant la pratique' },
  { v: 'grossesse', l: 'Grossesse' },
  { v: 'perte_emploi', l: "Perte d'emploi" },
  { v: 'autre', l: 'Autre motif légitime' },
];

export const MesDroits = () => {
  const { visible: legalVisible } = useLegalVisible();
  const [consents, setConsents] = useState({});
  const [busy, setBusy] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [motif, setMotif] = useState('');
  const [motifText, setMotifText] = useState('');

  const load = () => axios.get(`${API}/consents/my`, { withCredentials: true })
    .then(r => setConsents(r.data.consents || {})).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleConsent = async (type) => {
    const current = consents[type]?.granted || false;
    setBusy(type);
    try {
      await axios.post(`${API}/consents`, { type, granted: !current }, { withCredentials: true });
      toast.success(!current ? 'Consentement enregistré' : 'Consentement retiré');
      load();
    } catch { toast.error('Erreur lors de la mise à jour'); }
    finally { setBusy(''); }
  };

  const exportData = async () => {
    setBusy('export');
    try {
      const r = await axios.get(`${API}/account/my-data`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'mes_donnees_puro.json'; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erreur lors de l'export"); }
    finally { setBusy(''); }
  };

  const requestDeletion = async () => {
    if (!window.confirm('Confirmez-vous votre demande de suppression de compte ? Elle sera traitée par le studio.')) return;
    setBusy('delete');
    try {
      const r = await axios.post(`${API}/account/request-deletion`, {}, { withCredentials: true });
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(''); }
  };

  const submitCancel = async () => {
    setBusy('cancel');
    try {
      const r = await axios.post(`${API}/account/cancel-subscription`, {
        reason_type: motif || null, reason_text: motifText || null, legitimate_motive: !!motif,
      }, { withCredentials: true });
      toast.success(r.data.message, { description: `Date de fin prévisionnelle : ${r.data.end_date}` });
      setShowCancel(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(''); }
  };

  const ConsentRow = ({ type, label }) => {
    const c = consents[type];
    return (
      <div className="flex items-center justify-between py-2.5 border-b border-[#E9DDCC]">
        <div>
          <p className="text-sm text-[#493329]">{label}</p>
          {c && <p className="text-[10px] text-[#AE9885]">Dernière modification : {new Date(c.ts).toLocaleDateString('fr-FR')}</p>}
        </div>
        <Button size="sm" variant={c?.granted ? 'default' : 'outline'} disabled={busy === type}
          onClick={() => toggleConsent(type)}
          className={c?.granted ? 'rounded-full bg-[#C99A55] hover:bg-[#9A744C] text-white text-xs' : 'rounded-full border-[#E9DDCC] text-[#7A6455] text-xs'}
          data-testid={`consent-toggle-${type}`}>
          {c?.granted ? 'Activé' : 'Désactivé'}
        </Button>
      </div>
    );
  };

  return (
    <Card className="bg-white border-[#E9DDCC] rounded-3xl mb-6" data-testid="mes-droits-section">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Shield className="w-4 h-4 text-[#C99A55]" strokeWidth={1.5} />
          <h3 className="font-heading text-lg text-[#493329]">Mes données & mes droits</h3>
        </div>

        <ConsentRow type="newsletter" label="Recevoir les actualités PURO" />
        <ConsentRow type="droit_image" label="Autorisation photos / vidéos (droit à l'image)" />

        <div className="mt-4 space-y-2">
          <Button onClick={exportData} variant="outline" disabled={busy === 'export'}
            className="w-full rounded-2xl border-[#E9DDCC] text-[#493329] justify-start h-11" data-testid="export-data-btn">
            {busy === 'export' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2 text-[#C99A55]" strokeWidth={1.5} />}
            Télécharger mes données (RGPD)
          </Button>
          <Button onClick={() => setShowCancel(true)} variant="outline"
            className="w-full rounded-2xl border-[#E9DDCC] text-[#493329] justify-start h-11" data-testid="cancel-subscription-btn">
            <XCircle className="w-4 h-4 mr-2 text-[#C76542]" strokeWidth={1.5} /> Résilier mon abonnement
          </Button>
          <Button onClick={requestDeletion} variant="outline" disabled={busy === 'delete'}
            className="w-full rounded-2xl border-[#E9DDCC] text-[#7A6455] justify-start h-11" data-testid="request-deletion-btn">
            <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} /> Demander la suppression de mon compte
          </Button>
          {legalVisible && (
          <a href={`${API}/legal/retractation-form/pdf`} target="_blank" rel="noopener noreferrer" className="block">
            <Button variant="ghost" className="w-full rounded-2xl text-[#AE9885] justify-start h-10 text-xs">
              <FileText className="w-3.5 h-3.5 mr-2" strokeWidth={1.5} /> Formulaire de rétractation (PDF)
            </Button>
          </a>
          )}
        </div>

        <Dialog open={showCancel} onOpenChange={setShowCancel}>
          <DialogContent className="rounded-3xl max-w-md" data-testid="cancel-dialog">
            <DialogHeader>
              <DialogTitle className="font-heading text-[#493329]">Résilier mon abonnement</DialogTitle>
              <DialogDescription className="text-xs text-[#7A6455]">
                Vous recevrez un accusé de réception par email avec la date de fin effective. Pendant la période d'engagement, la résiliation anticipée nécessite un motif légitime validé par le studio.
              </DialogDescription>
            </DialogHeader>
            <select value={motif} onChange={e => setMotif(e.target.value)} data-testid="cancel-motif"
              className="flex h-11 w-full rounded-xl border border-[#E9DDCC] bg-white px-3 text-sm text-[#493329]">
              {MOTIFS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
            <Textarea value={motifText} onChange={e => setMotifText(e.target.value)}
              placeholder="Précisions (facultatif)" className="rounded-xl border-[#E9DDCC]" data-testid="cancel-text" />
            <Button onClick={submitCancel} disabled={busy === 'cancel'}
              className="w-full rounded-2xl bg-[#C76542] hover:bg-[#a84f30] text-white" data-testid="cancel-confirm-btn">
              {busy === 'cancel' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmer ma demande de résiliation'}
            </Button>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
